import { useCallback, useContext, useEffect, useState } from "react";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import ParamsContext from "context/params/paramsContext";
import RoutesContext from "context/routes/routesContext";
import TableContext from "context/table/tableContext";
import { FILTER_NAMES, isFilterOrSearch, useMediaQueries } from "shared";

import NoInitialResults from "./NoInitialResults";
import NoPermission from "./NoPermission";
import NoResultsFound from "./NoResultsFound";

const TableEmpty = () => {
    const { blue100, blue200, gray200 } = colors;

    const { closeStatistics } = useContext(RoutesContext);
    const { queryParams } = useContext(ParamsContext);

    const {
        hasNoPermissionToRead,
        isInnerTable,
        innerTableInitialParamNames,
        innerTableQueryParams,
        isTableLoading,
        noResultsReason
    } = useContext(TableContext);

    const { fromMd } = useMediaQueries();

    const isFilterOrSearchInUse = useCallback(() => {
        const queryParameters = isInnerTable
            ? (() => {
                  const copy = { ...innerTableQueryParams };

                  innerTableInitialParamNames.forEach(
                      param => delete copy[param]
                  );

                  return copy;
              })()
            : queryParams;

        const excludedParamNames = [
            FILTER_NAMES.RootCompany,
            FILTER_NAMES.CompanyId
        ];

        return Object.keys(queryParameters).some(
            param =>
                isFilterOrSearch(param) &&
                !excludedParamNames.includes(param as FILTER_NAMES)
        );
    }, [
        innerTableInitialParamNames,
        innerTableQueryParams,
        isInnerTable,
        queryParams
    ]);

    const [isFilterOrSearchUsed, setIsFilterOrSearchUsed] = useState(
        isFilterOrSearchInUse()
    );

    useEffect(() => {
        closeStatistics();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setIsFilterOrSearchUsed(isFilterOrSearchInUse());
    }, [isTableLoading, noResultsReason, isFilterOrSearchInUse]);

    const renderContent = () => {
        if (hasNoPermissionToRead) {
            return <NoPermission />;
        }

        return isFilterOrSearchUsed ? <NoResultsFound /> : <NoInitialResults />;
    };

    return (
        <div
            className="remaining-height"
            css={css({
                position: "relative"
            })}
        >
            <div
                className={isInnerTable ? undefined : "remaining-height"}
                css={css({
                    position: "absolute",
                    top: "0",
                    bottom: "0",
                    left: "0",
                    right: "0",
                    overflow: "auto"
                })}
            >
                <div
                    css={css({
                        height: "42px",
                        boxSizing: "border-box",
                        background: blue100,
                        borderTop: `1px solid ${blue200}`,
                        borderBottom: `1px solid ${blue200}`
                    })}
                />

                <div
                    css={css({
                        display: "flex",
                        flex: "1",
                        padding: "16px 0"
                    })}
                >
                    {renderContent()}
                </div>

                {!hasNoPermissionToRead && !isInnerTable && fromMd && (
                    <div
                        css={css({
                            height: "1px",
                            background: gray200,
                            marginBottom: "86px"
                        })}
                    />
                )}
            </div>
        </div>
    );
};

export default TableEmpty;
